import { apiWithCore, ShortOption } from 'store';
import { ApiEndpoints, Facility } from 'models';
//import { IntraopNoteKeyMap, ScheduleKeyMapItem, ScheduleMetadataMap } from 'pages';
import { StringLocale } from 'yup/lib/locale';

export interface SupplyChainDashboardResponse {
	total_supply_spend: number;
	current_inventory_cost: number;
	par_level_optimization: number;
	obsolete_inventory: number;
	missed_charges: number;
	inventory_turns: number;
}

export interface ObsoleteInventoryData {
	groupValue?: string | number | null;
	values: {
		x: string;
		y: number;
		id: number;
	}[];
}

interface ObsoleteInventoryResponse {
	data: ObsoleteInventoryData[] | undefined;
}

export interface ObsoleteInventoryDetailItem {
	area: string;
	department: string;
	raw_manufacturer_id: string;
	raw_item_id: number;
	description: string;
	luom_count: number;
	unit_of_measure: string;
	pluom_target_max: number;
	max_num_ordered_at_luom: number;
	num_charged_at_lowest_unit_of_measure: number;
	supplier: string;
	extended_cost: number;
}

export interface ObsoleteInventoryDetailsResponse {
	data: ObsoleteInventoryDetailItem[];
	group_by: string;
	// latest_date: string;
	// start_date: string;
}

export interface ParLevelInventoryData {
	groupValue?: string | number | null;
	values: {
		x: string;
		y: number;
		id: number;
	}[];
}

interface ParLevelInventoryResponse {
	data: ParLevelInventoryData[] | undefined;
}

export interface ParLevelInventoryDetailItem {
	area: string;
	department: string;
	raw_manufacturer_id: string;
	raw_item_id: number;
	description: string;
	luom_count: number;
	unit_of_measure: string;
	pluom_target_max: number;
	max_num_ordered_at_luom: number;
	num_charged_at_lowest_unit_of_measure: number;
	supplier: string;
	extended_cost: number;
}

export interface ParLevelInventoryDetailsResponse {
	data: ParLevelInventoryDetailItem[];
	group_by: string;
	// latest_date: string;
	// start_date: string;
}

export interface ChargeReconciliationItem {
	requisition_date: string;
	surgeon_name: string;
	specialty: string;
	case_id: string;
	vendor_name: string;
	item_description: string;
	quantity: number;
	unit_cost: number;
	extended_amount: number;
	usage_quantity: number;
	delta: number;
}

export interface ChargeReconciliationResponse {
	data: ChargeReconciliationItem[] | [];
}

/**
 * Provides endpoints for retrieving and setting block settings
 */
export const supplyChainApi = apiWithCore.injectEndpoints({
	endpoints: (build) => ({
		getSupplyChainDashboard: build.query<SupplyChainDashboardResponse, { facility_id: number | null }>({
			query: ({ facility_id }) => ({
				url: ApiEndpoints.GET_SUPPLY_CHAIN_DASHBOARD,
				params: {
					facility_id: facility_id !== null ? facility_id : undefined,
				},
			}),
			providesTags: ['Core'],
		}),
		getObsoleteInventory: build.query<
			ObsoleteInventoryResponse,
			{
				facility_id: number | null;
				groupby: string;
				sortby: string;
				viewby: string;
				filters: {
					days_of_week: string[];
					start_date: string;
					end_date: string;
				};
			}
		>({
			query: (body) => ({
				url: ApiEndpoints.GET_OBSOLETE_INVENTORY,
				method: 'POST',
				body,
			}),
			providesTags: ['Core'],
		}),
		getObsoleteInventoryDetails: build.query<
			ObsoleteInventoryDetailsResponse,
			{
				facility_id: number | null;
				viewby: string;
				filters: {
					days_of_week: string[];
					start_date: string;
					end_date: string;
					par_locations: ShortOption[];
					manufacturers: ShortOption[];
				};
			}
		>({
			query: (body) => ({
				url: ApiEndpoints.GET_OBSOLETE_INVENTORY_DETAILS,
				method: 'POST',
				body,
			}),
			providesTags: ['Core'],
		}),
		getParLevelInventory: build.query<
			ParLevelInventoryResponse,
			{
				facility_id: number | null;
				filters: {
					days_of_week: string[];
					start_date: string;
					end_date: string;
					par_locations: ShortOption[];
					manufacturers: ShortOption[];
				};
			}
		>({
			query: (body) => ({
				url: ApiEndpoints.GET_PAR_LEVEL_INVENTORY,
				method: 'POST',
				body,
			}),
			providesTags: ['Core'],
		}),
		getParLevelInventoryDetails: build.query<
			ParLevelInventoryDetailsResponse,
			{
				facility_id: number | null;
				viewby: string;
				filters: {
					days_of_week: string[];
					start_date: string;
					end_date: string;
					par_locations: ShortOption[];
					manufacturers: ShortOption[];
				};
			}
		>({
			query: (body) => ({
				url: ApiEndpoints.GET_PAR_LEVEL_INVENTORY_DETAILS,
				method: 'POST',
				body,
			}),
			providesTags: ['Core'],
		}),
		getChargeReconciliation: build.query<
			ChargeReconciliationResponse,
			{
				facility_id: number | null;
				healthsystem_id: number | null;
				filters: {
					days_of_week: string[];
					start_date: string;
					end_date: string;
					par_locations: ShortOption[];
					manufacturers: ShortOption[];
				};
			}
		>({
			query: (body) => ({
				url: ApiEndpoints.GET_CHARGE_RECONCILIATION,
				method: 'POST',
				body,
			}),
			providesTags: ['Core'],
		}),
	}),
});

export const {
	useGetSupplyChainDashboardQuery,
	useGetObsoleteInventoryQuery,
	useGetObsoleteInventoryDetailsQuery,
	useGetParLevelInventoryQuery,
	useGetParLevelInventoryDetailsQuery,
	useGetChargeReconciliationQuery,
} = supplyChainApi;

export const {
	endpoints: {
		getSupplyChainDashboard,
		getObsoleteInventory,
		getObsoleteInventoryDetails,
		getParLevelInventory,
		getParLevelInventoryDetails,
		getChargeReconciliation,
	},
} = supplyChainApi;
